/**
 * pages/dealer/routes.jsx
 * -----------------------
 * Route-wrapper pages for the dealer / builder dashboard. Each export wraps
 * the legacy `<DealerDashboard>` with a `defaultTab` + `embedded` prop so
 * the internal horizontal TabsList is hidden — the sidebar replaces it.
 *
 * ZERO business logic / API touched.
 *
 * v2.71.32 — Initial route-based dashboard migration.
 */

import React, { lazy, Suspense } from "react";
import { DashboardLayout } from "../../features/dashboard";
import { DashboardSkeleton } from "../../components/Skeletons";

const DealerDashboard = lazy(() => import("../DealerDashboard"));

function withDealer(tabValue, label, subtitle) {
  const Wrapped = function DealerRoutePage() {
    return (
      <DashboardLayout title={label} subtitle={subtitle}>
        <Suspense fallback={<DashboardSkeleton stats={4} rows={5} />}>
          <DealerDashboard defaultTab={tabValue} embedded />
        </Suspense>
      </DashboardLayout>
    );
  };
  Wrapped.displayName = `DealerRoute(${tabValue})`;
  return Wrapped;
}

export const DealerSubmit       = withDealer("submit",      "Add Property",  "Submit a new listing");
export const DealerListings     = withDealer("submissions", "My Listings",   "Your properties");
export const DealerLeads        = withDealer("enquiries",   "Leads",         "Inquiries and callbacks");
export const DealerEarnings     = withDealer("commission",  "Earnings",      "Commission and payouts");
export const DealerKYC          = withDealer("kyc",         "KYC",           "Verification status");
export const DealerProjects     = withDealer("projects",    "Projects",      "Builder projects (if applicable)");
export const DealerExploreRoute = withDealer("explore",     "Marketplace",   "Browse other listings — owner & exact location hidden");
export const DealerPlan         = withDealer("plan",        "Subscription",  "Plan, quota, billing");

/** Legacy `?tab=` → new route path. */
export const DEALER_TAB_TO_ROUTE = {
  submit:      "/dealer/submit",
  submissions: "/dealer/listings",   // friendly rename
  enquiries:   "/dealer/leads",
  leads:       "/dealer/leads",
  commission:  "/dealer/earnings",
  kyc:         "/dealer/kyc",
  projects:    "/dealer/projects",
  explore:     "/dealer/explore",
  plan:        "/dealer/plan",
};

/** Inverse map for the embedded DealerDashboard onTabChange — when it sets
 *  `tab=submissions` we want the URL to land on `/dealer/listings`. */
export const DEALER_ROUTE_TO_TAB = Object.fromEntries(
  Object.entries(DEALER_TAB_TO_ROUTE).map(([k, v]) => [v.replace("/dealer/", ""), k])
);
